import { Check, Radio } from "lucide-react";
import { cn } from "@/v2/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/v2/components/ui/dropdown-menu";
import type { Channel } from "../../Events/types";
import {
  channelFilterSummary,
  isChannelSelected,
  NO_CHANNELS_SELECTED,
  toggleChannelSelection,
} from "../channelFilter";
import { FilterTrigger } from "./FilterTrigger";

interface Props {
  channels: Channel[];
  value: string;
  onChange: (next: string) => void;
}

/**
 * Filters the library's games by broadcast channel, the same way the /vod
 * page's sidebar does (same value convention, the same API `channel` param
 * via useVodGames, and the same checkbox-plus-logo row and Select/Clear All
 * treatment as FilterSidebar), just as a compact dropdown instead of a
 * sidebar list.
 */
export function ChannelFilterDropdown({ channels, value, onChange }: Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterTrigger icon={Radio} label={channelFilterSummary(channels, value)} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" style={{ maxHeight: 320, overflowY: "auto", minWidth: 220 }}>
        <div className="flex items-center justify-between" style={{ padding: "4px 8px" }}>
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">CHANNELS</span>
          <div className="flex items-center" style={{ gap: 12 }}>
            <button
              type="button"
              className="text-xs font-semibold text-destructive hover:underline"
              onClick={() => onChange(NO_CHANNELS_SELECTED)}
            >
              Clear All
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-action hover:underline"
              onClick={() => onChange("")}
            >
              Select All
            </button>
          </div>
        </div>
        <DropdownMenuSeparator />
        {channels.map((channel) => {
          const id = String(channel.channel_id);
          const checked = isChannelSelected(value, id);
          return (
            <DropdownMenuCheckboxItem
              key={id}
              checked={checked}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => onChange(toggleChannelSelection(channels, value, id))}
              hideIndicator
            >
              <span
                aria-hidden
                className={cn(
                  "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md",
                  checked ? "bg-action" : "border border-input bg-card",
                )}
              >
                {checked && <Check className="text-white" strokeWidth={3} size={12} />}
              </span>
              {channel.channel_logo_url ? (
                <img
                  src={channel.channel_logo_url}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <span
                  aria-hidden
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-950"
                >
                  <span className="font-bold text-white" style={{ fontSize: 7 }}>
                    {channel.channel_name?.slice(0, 4).toUpperCase() ?? ""}
                  </span>
                </span>
              )}
              <span className="truncate">{channel.channel_name}</span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
